import { useNavigate } from "react-router-dom";
import { Masthead } from "@/components/Masthead";
import { Footer } from "@/components/Footer";
import { Smartphone, Server, Mail, Trash2, Clock, MessageCircle } from "lucide-react";

const SectionHeading = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-4 mb-4">
    <div className="shrink-0 mt-1 p-2.5 rounded-lg bg-scoop-blue/10 text-scoop-blue">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h2 className="font-display text-xl font-bold text-scoop-ink leading-tight">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  </div>
);

const Pill = ({ children }) => (
  <span className="inline-block text-[0.65rem] font-bold tracking-widest uppercase px-2 py-0.5 rounded bg-scoop-blue/10 text-scoop-blue">
    {children}
  </span>
);

export const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="scoop-page min-h-screen bg-scoop-cream" data-testid="privacy-page">
      <Masthead
        activeCategory=""
        onCategoryChange={(cat) => navigate(cat && cat !== "today" ? `/?category=${cat}` : "/")}
      />

      <div className="max-w-[760px] mx-auto px-5 md:px-8 py-10 md:py-14">
        {/* Hero */}
        <div className="mb-10 pb-8 border-b border-scoop-border">
          <Pill>Privacy Notice</Pill>
          <h1 className="font-display text-3xl md:text-4xl font-black text-scoop-ink mt-3 leading-tight">
            What we store, and what we <em className="italic text-scoop-blue">don&rsquo;t</em>
          </h1>
          <p className="text-gray-600 mt-3 leading-relaxed">
            The Scoop is read by children, so the short version matters most:{" "}
            <strong>we do not collect personal information from kids.</strong> Children
            never create an account, and everything the app remembers about a child stays
            on their own device. The longer version is below.
          </p>
          <p className="text-sm text-gray-600 mt-3 leading-relaxed">
            This notice covers <strong>The Scoop</strong> &mdash; the website and the
            iOS app. Dreamstories, our separate storytelling app, works differently and
            has its own notice.
          </p>
          <p className="text-xs text-scoop-muted mt-3">Last updated: 28 July 2026</p>
        </div>

        {/* On the device */}
        <section className="mb-10">
          <SectionHeading
            icon={Smartphone}
            title="What stays on the device"
            subtitle="Never uploaded, never seen by us"
          />
          <div className="space-y-3 text-[15px] text-gray-700 leading-relaxed pl-1">
            <p>
              In The Scoop app, these are stored locally on the phone or tablet and are
              never sent to our servers:
            </p>
            <ul className="space-y-2 list-disc pl-5">
              <li>The child&rsquo;s first name, if one was entered &mdash; it&rsquo;s optional</li>
              <li>Their chosen avatar</li>
              <li>Which stories they&rsquo;ve read and how far they got</li>
              <li>Which stories they saved</li>
              <li>Badges earned and days read</li>
              <li>Text size and read-aloud preferences</li>
            </ul>
            <p>
              Two things on that list leave the device in specific, limited
              circumstances, and we&rsquo;d rather spell them out than let the sentence
              above overstate itself:
            </p>
            <ul className="space-y-2 list-disc pl-5">
              <li>
                <strong>Saved stories, only when a parent shares the list.</strong>{" "}
                Creating a share link needs the server to know which stories are on it,
                so at that moment the list of story IDs is sent and attached to the
                parent&rsquo;s account. Nothing else about the child goes with it. If no
                one ever shares a list, the saves never leave the device.
              </li>
              <li>
                <strong>Poll answers are counted on the server.</strong> Tapping an
                answer sends which option was chosen so it can be added to a running
                total. No identifier is sent or stored with it &mdash; we keep a count
                per answer, not a record of who picked what. Which answer a particular
                child chose is remembered only on their own device.
              </li>
            </ul>
            <p>
              A parent can see this list and erase all of it from{" "}
              <strong>Parent Zone &rarr; Your kid&rsquo;s data &rarr; Erase kid data</strong>.
              Deleting the app also removes it. Because it never leaves the device, we
              cannot retrieve it, restore it, or hand it to anyone.
            </p>
          </div>
        </section>

        {/* On our servers */}
        <section className="mb-10">
          <SectionHeading icon={Server} title="What our servers see" subtitle="And what we do with it" />
          <div className="space-y-3 text-[15px] text-gray-700 leading-relaxed pl-1">
            <p>
              When the app or site asks for a story, that request reaches our servers
              like any web request &mdash; which means it includes an IP address and
              basic technical details. We use this to deliver the content and to keep
              the service working and secure. We do not use it to build a profile,
              target anything, or identify a reader, and we don&rsquo;t combine it with
              anything else.
            </p>
            <p>
              <strong>We use no third-party analytics, session recording, advertising
              code or tracking pixels</strong> &mdash; not in the app, not on the site,
              not in the parent-only areas. The mobile app loads no third-party code at
              all.
            </p>
            <p>
              One piece of honesty about the website: it is built and hosted on the
              Emergent platform, and the hosted page includes a script from that
              platform (<code className="text-[13px]">assets.emergent.sh</code>). It is
              part of the hosting environment rather than anything we added to measure
              readers, but it is code we do not write, so we will not claim the page is
              free of third-party code. We are documenting exactly what it does and will
              update this notice with the answer; if it turns out to observe readers, we
              will remove it.
            </p>
            <p>
              Our moderation log records decisions about <em>articles</em> &mdash; which
              story was published or blocked and why. It contains nothing about who read
              them.
            </p>
          </div>
        </section>

        {/* Parent accounts */}
        <section className="mb-10">
          <SectionHeading icon={Mail} title="Parent accounts" subtitle="The only accounts that exist" />
          <div className="space-y-3 text-[15px] text-gray-700 leading-relaxed pl-1">
            <p>
              A parent may sign in with Google to turn on the daily digest email or to
              share a reading list. In that case we store the name and email address
              their Google account gives us, and a session token so they stay signed in.
              That&rsquo;s it. We never sell or share email addresses, and every digest
              has a one-click unsubscribe.
            </p>
            <p>
              <strong>A child&rsquo;s name is never included in a shared list</strong>{" "}
              &mdash; lists are titled &ldquo;Our Reading List&rdquo; and contain only
              the stories themselves. The link can be opened by anyone who has it, so we
              keep the child out of it entirely.
            </p>
            <p>
              The list does record <em>the parent</em> who created it, and their name is
              shown to anyone opening the link, alongside the stories. Share links do not
              currently expire and cannot yet be revoked &mdash; we are adding both. Until
              then, treat a share link as permanent and only send it to people you trust.
            </p>
            <p>
              Story reports sent through the &ldquo;Something not right? Tell us&rdquo;
              link are anonymous. We record the story, the reason chosen and any note
              written &mdash; not who sent it.
            </p>
          </div>
        </section>

        {/* Retention */}
        <section className="mb-10">
          <SectionHeading icon={Clock} title="How long we keep things" subtitle="Retention policy" />
          <div className="space-y-3 text-[15px] text-gray-700 leading-relaxed pl-1">
            <ul className="space-y-2 list-disc pl-5">
              <li>
                <strong>On-device kid data:</strong> kept until a parent erases it or the
                app is deleted. We never hold a copy.
              </li>
              <li>
                <strong>Parent account details:</strong> currently kept until you ask us
                to delete them. We are adding a self-service delete; today it is a manual
                request to the address below, which we action within 30 days.
              </li>
              <li>
                <strong>Shared reading lists:</strong> currently kept indefinitely. Expiry
                and revocation are being added.
              </li>
              <li>
                <strong>Moderation records:</strong> kept for 180 days, then removed
                automatically. They are how we show the safety system works and contain
                no reader information.
              </li>
              <li>
                <strong>Story reports:</strong> kept while we run the service, so we can
                show a problem was acted on. They carry no identifier for whoever sent
                them.
              </li>
              <li>
                <strong>Server request logs:</strong> handled by our hosting provider. We
                are confirming the exact retention period and will state it here rather
                than leave it vague.
              </li>
            </ul>
            <p className="text-[13px] text-gray-500">
              Where this section says &ldquo;currently&rdquo;, it means the practice is
              real but weaker than we want it to be. We would rather publish that than a
              policy we do not yet meet.
            </p>
          </div>
        </section>

        {/* Rights */}
        <section className="mb-10">
          <SectionHeading icon={Trash2} title="Deleting your data" subtitle="How to ask, and what happens" />
          <div className="space-y-3 text-[15px] text-gray-700 leading-relaxed pl-1">
            <p>
              A child&rsquo;s data can be erased immediately from the Parent Zone &mdash;
              no request needed, because we don&rsquo;t hold it.
            </p>
            <p>
              For a parent account, write to us and we&rsquo;ll delete the account, the
              email address and any shared lists created from it. Parents also have the
              right to review what we hold about their family and to refuse any further
              collection &mdash; though in practice, for children, there is nothing to
              review because there is nothing collected.
            </p>
          </div>
        </section>

        {/* Contact */}
        <div className="bg-[#F0FBF5] border-l-[3px] border-scoop-sage rounded-r p-5 mt-10">
          <div className="flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-scoop-sage shrink-0 mt-0.5" />
            <p className="text-sm text-[#1A5C3A] leading-relaxed">
              Questions about any of this, or a deletion request? Email{" "}
              <a
                href="mailto:privacy@thescoop.is"
                className="font-bold underline underline-offset-2"
              >
                privacy@thescoop.is
              </a>{" "}
              and a person will answer. If something on this page doesn&rsquo;t match
              what the product actually does, tell us &mdash; that&rsquo;s a bug and
              we&rsquo;ll fix it.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
